/**
 * Condition evaluation.
 *
 * Conditions are plain data so that content authors (and content generators)
 * can express requirements without writing code, and so the validator can
 * check every predicate ahead of time.
 */

import type {
  Condition,
  CompareOp,
  PlayerState,
} from './types';
import { EDUCATION_RANK } from './types';
import { peopleOfKind, queryNumber, queryString } from './derived';

export function compare(a: number, op: CompareOp, b: number): boolean {
  switch (op) {
    case '>':
      return a > b;
    case '>=':
      return a >= b;
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '==':
      return a === b;
    case '!=':
      return a !== b;
    default:
      return false;
  }
}

export function evaluate(state: PlayerState, condition: Condition): boolean {
  switch (condition.k) {
    case 'num': {
      const v = queryNumber(state, condition.path);
      if (v === undefined) return false;
      return compare(v, condition.op, condition.value);
    }
    case 'str': {
      const v = queryString(state, condition.path);
      if (v === undefined) return false;
      return condition.op === '==' ? v === condition.value : v !== condition.value;
    }
    case 'stage':
      return condition.anyOf.includes(state.stage);
    case 'age': {
      if (condition.min !== undefined && state.age < condition.min) return false;
      if (condition.max !== undefined && state.age > condition.max) return false;
      return true;
    }
    case 'flag': {
      const want = condition.value ?? true;
      return (state.flags[condition.flag] ?? false) === want;
    }
    case 'marital':
      return condition.anyOf.includes(state.family.maritalStatus);
    case 'careerStatus':
      return condition.anyOf.includes(state.career.status);
    case 'hasPerson':
      return peopleOfKind(state, condition.kind).length >= (condition.min ?? 1);
    case 'hasCredential':
      return state.education.credentials.includes(condition.id);
    case 'educationAtLeast':
      return EDUCATION_RANK[state.education.level] >= EDUCATION_RANK[condition.level];
    case 'skill':
      return compare(state.skills[condition.skill], condition.op, condition.value);
    case 'trait':
      return compare(state.traits[condition.trait], condition.op, condition.value);
    case 'habit':
      return compare(state.habits[condition.habit], condition.op, condition.value);
    case 'burnout':
      return condition.anyOf.includes(state.burnout.stage);
    case 'eventSeen': {
      const seen = (state.eventMemory.counts[condition.id] ?? 0) > 0;
      return seen === (condition.value ?? true);
    }
    case 'intention': {
      const found = state.inertia.intentions.some(
        (i) =>
          (condition.id === undefined || i.id === condition.id) &&
          (condition.topic === undefined || i.topic === condition.topic),
      );
      return found === condition.present;
    }
    case 'aspiration': {
      const asp = state.aspirations.find((a) => a.defId === condition.defId);
      if (condition.present === false) return asp === undefined;
      if (!asp) return false;
      if (condition.status && asp.status !== condition.status) return false;
      return true;
    }
    case 'not':
      return !evaluate(state, condition.of);
    case 'all':
      return condition.of.every((c) => evaluate(state, c));
    case 'any':
      return condition.of.some((c) => evaluate(state, c));
    default:
      return false;
  }
}

export function evaluateAll(state: PlayerState, conditions: Condition[] | undefined): boolean {
  if (!conditions || conditions.length === 0) return true;
  return conditions.every((c) => evaluate(state, c));
}

export function evaluateAny(state: PlayerState, conditions: Condition[] | undefined): boolean {
  if (!conditions || conditions.length === 0) return false;
  return conditions.some((c) => evaluate(state, c));
}

/* ------------------------------------------------------------------ *
 * Human-readable rendering, used by the UI to explain locked choices
 * ------------------------------------------------------------------ */

const OP_WORD: Record<CompareOp, string> = {
  '>': 'above',
  '>=': 'at least',
  '<': 'below',
  '<=': 'at most',
  '==': 'exactly',
  '!=': 'not',
};

const MONEY_PATHS = new Set(['cash', 'salary', 'income', 'expenses', 'netWorth', 'debt', 'investments', 'retirementAssets', 'businessRevenue', 'businessValuation']);

function fmtValue(path: string, value: number): string {
  if (MONEY_PATHS.has(path)) return `$${value.toLocaleString('en-US')}`;
  return String(value);
}

const PATH_LABEL: Record<string, string> = {
  cash: 'cash',
  netWorth: 'net worth',
  runwayMonths: 'months of runway',
  roleLevel: 'seniority',
  yearsExperience: 'years of experience',
  careerReputation: 'professional reputation',
  socialHealth: 'relationship health',
  supportNetwork: 'support network',
  financialSecurity: 'financial security',
  educationRank: 'education level',
  childCount: 'children',
  intentionCount: 'open intentions',
};

export function describeCondition(condition: Condition): string {
  switch (condition.k) {
    case 'num':
      return `${PATH_LABEL[condition.path] ?? condition.path} ${OP_WORD[condition.op]} ${fmtValue(condition.path, condition.value)}`;
    case 'str':
      return `${condition.path} ${condition.op === '==' ? 'is' : 'is not'} ${condition.value}`;
    case 'stage':
      return `during ${condition.anyOf.join(' or ')}`;
    case 'age': {
      if (condition.min !== undefined && condition.max !== undefined) return `age ${condition.min}–${condition.max}`;
      if (condition.min !== undefined) return `age ${condition.min}+`;
      if (condition.max !== undefined) return `age under ${condition.max + 1}`;
      return 'any age';
    }
    case 'flag':
      return `${condition.value === false ? 'not ' : ''}${condition.flag.replace(/[._]/g, ' ')}`;
    case 'marital':
      return `${condition.anyOf.join(' or ')}`;
    case 'careerStatus':
      return `working status: ${condition.anyOf.join(' or ')}`;
    case 'hasPerson':
      return `${condition.min ?? 1}+ ${condition.kind}${(condition.min ?? 1) > 1 ? 's' : ''}`;
    case 'hasCredential':
      return `credential: ${condition.id}`;
    case 'educationAtLeast':
      return `${condition.level} or higher`;
    case 'skill':
      return `${condition.skill} skill ${OP_WORD[condition.op]} ${condition.value}`;
    case 'trait':
      return `${condition.trait} ${OP_WORD[condition.op]} ${condition.value}`;
    case 'habit':
      return `${condition.habit} habit ${OP_WORD[condition.op]} ${condition.value}`;
    case 'burnout':
      return `burnout: ${condition.anyOf.join(' or ')}`;
    case 'eventSeen':
      return condition.value === false ? 'not experienced before' : 'experienced before';
    case 'intention':
      return condition.present ? `an open ${condition.topic ?? ''} intention`.trim() : 'no open intention';
    case 'aspiration':
      return `${condition.defId} ${condition.status ?? 'active'}`;
    case 'not':
      return `not (${describeCondition(condition.of)})`;
    case 'all':
      return condition.of.map(describeCondition).join(' and ');
    case 'any':
      return condition.of.map(describeCondition).join(' or ');
    default:
      return 'unknown requirement';
  }
}

/** The first unmet requirement, for display on a locked choice. */
export function firstUnmet(state: PlayerState, conditions: Condition[] | undefined): string | null {
  if (!conditions) return null;
  for (const c of conditions) {
    if (!evaluate(state, c)) return describeCondition(c);
  }
  return null;
}
